import {
  type AfterViewInit,
  Component,
  ComponentFactoryResolver,
  type ComponentRef,
  inject,
  input,
  type OnChanges,
  type OnDestroy,
  type OnInit,
  output,
  type SimpleChanges,
  viewChild,
} from "@angular/core";
import { Button, ButtonIcon, Icon } from "@ui";
import type { Subscription } from "rxjs";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import { TRANSFORMER_STATE } from "./abstract-transformer.service";
import { ColumnTypeInstructionsTooltipComponent } from "./column-type/column-type-instructions-tooltip.component";
import { ColumnTypeTransformerService } from "./column-type/column-type-transformer.service";
import { FindReplaceTransformerService } from "./find-replace/find-replace-transformer.service";
import { SampleTransformerService } from "./sample/sample-transformer.service";
import { SentenceChunkTransformerService } from "./sentence-chunk/sentence-chunk-transformer.service";
import { ToolbarDirective } from "./toolbar.directive";
import type { TransformerToolbar } from "./toolbar.interface";
import type { Transformer } from "./transformer.interface";
import { TransformerService } from "./transformer.service";
import { TransformerRegistryService } from "./transformer-registry.service";

@Component({
  selector: "app-transformer-toolbar",
  template: `
    <div class="p-stack p-flex-between p-h-full">
      <div class="p-flex p-flex-col p-flex-grow-1 p-min-h-0">
        @if (!transformer) {
          <div class="p-flex-row-xs p-flex-center-y p-p-xxs">
            <p-button-icon-ghosted-lg icon="arrow-left" aria-label="Back" (click)="back.emit()" />
            <span class="p-text-body">Refine data</span>
          </div>
          <div class="p-divider"></div>
          @for (xformer of transformers; track xformer) {
            <div class="p-flex p-flex-center-y transformer"               
                [attr.disabled]="enabled() ? null : ''"
                (click)="loadTransformer(xformer)">
              <div class="p-flex-col-xxs p-py-sm p-ps-sm">
                <div class="p-flex-row-xs p-flex-center-y">
                  <p-icon-lg class="p-color-text-tertiary" [icon]="iconFor(xformer)" />
                  <span class="p-text-body p-color-text-secondary">{{ xformer.name }}</span>
                  @if (xformer.name === "Column types") {
                  <app-column-type-instructions-tooltip />
                  }
                </div>
                @if (xformer.name === "Column types") {
                  <span class="p-text-footnote p-color-text-tertiary">
                    Set the correct data type for each column.
                  </span>
                } @else {
                  <span class="p-text-footnote p-color-text-tertiary">{{ descriptionFor(xformer) }}</span>
                }
              </div>
              <p-icon class="p-color-text-disabled p-p-xxs" icon="chevron-right" />
          </div>
          <div class="p-divider"></div>
          }
        }
        <div class="transformer-toolbar-host p-flex p-flex-col p-flex-grow-1 p-min-h-0" transformerToolbarHost></div>
      </div>

      @if (!transformer) {
      <div class="p-content p-content--md">
        <p-button-primary class="p-w-full" [disabled]="!enabled()" (click)="formatRequested.emit()">
          Format data
        </p-button-primary>
      </div>
      }
    </div>
    `,
  styleUrls: ["./toolbar.component.scss"],
  imports: [Button, ButtonIcon, Icon, ColumnTypeInstructionsTooltipComponent, ToolbarDirective],
})
export class ToolbarComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private service = inject(TransformerService);
  private registry = inject(TransformerRegistryService);
  private componentFactoryResolver = inject(ComponentFactoryResolver);
  private findReplace = inject(FindReplaceTransformerService);
  private columnTypes = inject(ColumnTypeTransformerService);
  private sampler = inject(SampleTransformerService);
  private chunker = inject(SentenceChunkTransformerService);

  readonly corpus = input.required<Corpus>();
  readonly enabled = input(true);
  readonly formatRequested = output<void>();
  // emitted when the user wants to return to the previous ingest modal step
  readonly back = output<void>();

  transformer: Transformer | null = null; // active transformer
  transformerSubscription: Subscription | null = null;
  toolComponentRef: ComponentRef<TransformerToolbar> | null = null;
  showSuccess = false;

  readonly toolbarHost = viewChild.required(ToolbarDirective);

  private subscription: Subscription | null = null;

  transformers: Transformer[]; // available transformers

  iconFor(transformer: Transformer): string {
    switch (transformer.name) {
      case "Column types":
        return "table";
      case "Find & replace":
        return "cycle";
      case "Sample":
        return "filter-list";
      default:
        return "copy";
    }
  }

  descriptionFor(transformer: Transformer): string {
    switch (transformer.name) {
      case "Column types":
        return "Set the correct data type for each column.";
      case "Find & replace":
        return "Find phrases and replace, remove or delete the entire row containing them.";
      case "Sample":
        return "Select a random sample of rows from the data set.";
      default:
        return "Break your documents into chunks based on sentence quantity.";
    }
  }

  constructor() {
    const findReplace = this.findReplace;
    const columnTypes = this.columnTypes;
    const sampler = this.sampler;
    const chunker = this.chunker;

    this.transformers = [columnTypes, findReplace, sampler, chunker];
    this.transformers.forEach((t) => {
      this.registry.register(t);
    });
  }

  ngOnInit(): void {
    this.service.reset();
    this.transformers.forEach((t) => {
      t.reset();
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["corpus"] && this.toolComponentRef) {
      this.toolComponentRef.setInput("corpus", this.corpus());
    }
    if (this.corpus() && this.toolbarHost()) {
      this.loadToolbar(this.service.activeTransformer);
    }
  }

  ngAfterViewInit() {
    if (!this.subscription) {
      this.subscription = this.service.activeTransformer$.subscribe((xform) => this.loadToolbar(xform));
    }
    if (this.corpus()) {
      this.loadToolbar(this.service.activeTransformer);
    }
  }

  private loadToolbar(transformer: Transformer | null) {
    if (!this.toolbarHost() || !this.corpus()) {
      return;
    }

    setTimeout(() => {
      const prevTransformer = this.transformer;
      this.transformer = transformer;
      this.toolComponentRef = null;

      const viewContainerRef = this.toolbarHost().viewContainerRef;
      viewContainerRef.clear();
      if (transformer) {
        // Show the toolbar
        const tool = transformer.toolbar!;
        const factory = this.componentFactoryResolver.resolveComponentFactory(tool.component);
        this.toolComponentRef = viewContainerRef.createComponent<TransformerToolbar>(factory);
        this.toolComponentRef.setInput("corpus", this.corpus());

        // Close the toolbar when it completes successfully
        if (this.transformerSubscription) {
          // shouldn't happen
          console.log("Unexpected lingering subscription");
          this.transformerSubscription.unsubscribe();
          this.transformerSubscription = null;
        }
        let prevState = transformer.service.state;
        this.transformerSubscription = transformer.service.state$.subscribe((state) => {
          if (prevState !== TRANSFORMER_STATE.DONE && state === TRANSFORMER_STATE.DONE) {
            this.close();
            // this.loadToolbar(null);
          }
          prevState = state; // I know it's impure to capture like this, but it works :)
        });
      } else {
        if (prevTransformer) {
          if (prevTransformer.service.state === TRANSFORMER_STATE.DONE) {
            this.showSuccessMessage();
          }
        }
        if (this.transformerSubscription) {
          this.transformerSubscription.unsubscribe();
          this.transformerSubscription = null;
        }
      }
    });
  }

  private showSuccessMessage() {
    this.showSuccess = true;
    setTimeout(() => {
      this.showSuccess = false;
    }, 7000);
  }

  loadTransformer(transformer: Transformer) {
    this.service.transformCorpus(this.corpus(), transformer.id);
  }

  close() {
    this.service.cancel();
    // this.gridDataService.loadPage(0);
  }
}
